import { BottomNavigation, BottomNavigationAction, createStyles } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { i18n, Icons } from '@nutrien/cxp-components';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import DiscardDraftModal from '@/components/DiscardDraftModal';
import { useFlags } from '@/featureFlags/useFlags';
import { useMst } from '@/mobx-models/Root';
import { useNotification } from '@/utilities';

import TabBarConfig from './TabBarConfig.json';

const useStyles = makeStyles(() =>
  createStyles({
    hidden: {
      display: 'none',
    },
    root: {
      position: 'absolute',
      bottom: '0px',
      width: '100%',
      justifyContent: 'space-between',
      height: '75px',
      boxShadow: '0px 0px 16px 0px rgba(0, 0, 0, 1)',
      zIndex: 15,
    },
  }),
);

const checkIfVisible = (pathname: string) => {
  return TabBarConfig.blacklistedPaths.indexOf(pathname) === -1;
};

const TabBar: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { errorNotification } = useNotification();
  const { equipment } = useMst();

  const [discardOpen, setDiscardOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const flags = useFlags() as object as Record<string, unknown>;

  const showableTabs = useMemo(() => {
    return TabBarConfig.items.filter(item => {
      if (!item.featureFlag) return true;
      if ('featureFlag' in item && flags[item.featureFlag] === true) return true;
      return false;
    });
  }, [flags]);

  const normalize = (p: string) => (p ? p.replace(/\/+$/, '') : '');

  const getActiveTabNumber = (pathname: string) => {
    const normPath = normalize(pathname);
    let result = -1;
    showableTabs.forEach((item, index) => {
      const itemPath = normalize(item.path);
      // match exact or when pathname starts with the item's path (handles nested routes)
      if (normPath === itemPath || normPath.startsWith(itemPath + '/')) {
        result = index;
      }
    });
    return result;
  };

  const [visible, setVisible] = useState(checkIfVisible(location.pathname));
  const [value, setValue] = useState(getActiveTabNumber(location.pathname));

  useEffect(() => {
    // Recompute when either pathname or the available tabs change
    const visibleValue = checkIfVisible(location.pathname);
    const activeTabNumber = getActiveTabNumber(location.pathname);
    setVisible(visibleValue);
    setValue(activeTabNumber);
  }, [location.pathname, showableTabs]);

  const getIconFromName = (name: string) => {
    switch (name) {
      case 'FileTextFeather':
        return <Icons.FileTextFeather color="white" />;
      case 'ShiftStartIcon':
        return <Icons.ShiftStartIcon color="white" />;
      case 'CheckCircleFeather':
        return <Icons.CheckCircleFeather color="white" />;
      case 'TruckFeather':
        return <Icons.TruckFeather color="white" />;
      case 'CalendarFeather':
        return <Icons.CalendarFeather color="white" />;
      case 'ArrowRightFeather':
        return <Icons.ArrowRightFeather color="white" />;
      case 'ShiftEndIcon':
        return <Icons.ShiftEndIcon color="white" />;
      default:
        return <Icons.HelpCircleFeather color="white" />;
    }
  };

  const handleTabClick = (item: string) => {
    if (equipment.editIndexValue !== null) {
      setSelectedItem(item);
      setDiscardOpen(true);
    } else {
      history.push(item);
    }
  };

  return (
    <>
      <BottomNavigation
        value={value}
        onChange={(event, newValue) => {
          setValue(newValue);
        }}
        showLabels
        className={visible ? classes.root : classes.hidden}
      >
        {showableTabs.map(item => {
          return (
            <BottomNavigationAction
              key={item.key}
              label={item.label}
              icon={getIconFromName(item.iconName)}
              data-cy={`tabbar-${item.key}`}
              data-testid={`tabbar-${item.key}`}
              onClick={() => handleTabClick(item.path)}
              id={`tabbar-${item.label}`}
            />
          );
        })}
      </BottomNavigation>

      <DiscardDraftModal
        open={discardOpen}
        onCancel={() => {
          setValue(getActiveTabNumber(location.pathname));
          setDiscardOpen(false);
        }}
        onDiscard={() => {
          setDiscardOpen(false);
          equipment.setEditIndex(null);
          if (selectedItem) {
            history.push(selectedItem);
          }
          errorNotification(i18n.t('Draft discarded'));
        }}
      />
    </>
  );
};

export default TabBar;
